<!--
  查询条件渲染组件
  封装 gljy-table 中顶部/底部两处重复的查询条件渲染逻辑,
  通过 isBottom 区分顶部/底部展示:
    - 顶部 (isBottom=false) 渲染 index < topShowWhereFieldNumber 且未隐藏的字段,
      使用 topSearchItemBefore/topSearchItemAfter 插槽,无额外 class;
    - 底部 (isBottom=true)  渲染 index >= topShowWhereFieldNumber 且未隐藏的字段,
      使用 searchItemBefore/searchItemAfter 插槽,控件带 search-bottom-item 样式类。
  queryWhere 传入 reactive 对象,子组件内 v-model 修改会自动同步到父组件。
-->
<template>
  <template v-for="(item, index) in fields" :key="item.fieldName">
    <template v-if="isFieldVisible(item, index)">
      <slot :name="slotBeforeName" :field="item" :index="index"></slot>
      <gljy-input
        v-if="item.type == 'String' || item.type == 'Number'"
        :class="itemClassName"
        v-model="queryWhere[item.fieldName]"
        @change="onFieldChange(item)"
        :placeholder="item.placeholder"
        :style="item.style ? item.style : { width: '150px', marginRight: '10px' }" />
      <gljy-select
        v-else-if="item.type == 'gljySelect'"
        :class="itemClassName"
        :allowClear="true"
        :style="item.style ? item.style : { width: '180px', marginRight: '10px' }"
        v-model:value="queryWhere[item.fieldName]"
        :placeholder="item.placeholder"
        @change="onFieldChange(item)"
        :type="item.selectSetting.type"
        :queryFunction="item.selectSetting.queryFunction"
        :fieldNames="item.selectSetting.fieldNames"
        :dataSource="item.selectSetting.dataSource"
        :url="item.selectSetting.url"
        :extendQueryWhere="item.selectSetting.extendQueryWhere"
        :mode="item.selectSetting.mode"
        :clearable="item.selectSetting.clearable"
        @resultQueryData="item.selectSetting.resultQueryData"
        :showSearch="item.selectSetting.showSearch"
        :optionFilterProp="item.selectSetting.optionFilterProp">
      </gljy-select>
      <gljy-tree-select
        v-else-if="item.type == 'gljyTreeSelect'"
        :class="itemClassName"
        :style="item.style ? item.style : { width: '280px', marginRight: '10px' }"
        v-model:value="queryWhere[item.fieldName]"
        :placeholder="item.placeholder"
        :queryFunction="item.treeSetting.queryFunction"
        :fieldNames="item.treeSetting.fieldNames"
        :url="item.treeSetting.url"
        :extendQueryWhere="item.treeSetting.extendQueryWhere"
        :mode="item.treeSetting.mode"
        :treeNodeFilterProp="item.treeSetting.treeNodeFilterProp"
        allowClear
        @change="onFieldChange(item)"></gljy-tree-select>
      <gljy-date-picker
        v-else-if="item.type == 'gljyDatePicker'"
        :class="itemClassName"
        :style="item.style ? item.style : { marginRight: '10px' }"
        v-model:value="queryWhere[item.fieldName]"
        :placeholder="item.placeholder"
        :type="item.prickerSetting.type"
        @change="onFieldChange(item)"
        v-model:startDate="queryWhere[item.prickerSetting.startDate ? item.prickerSetting.startDate.fieldName : '']"
        v-model:endDate="queryWhere[item.prickerSetting.endDate ? item.prickerSetting.endDate.fieldName : '']"
        :format="item.prickerSetting.format"
        :valueFormat="item.prickerSetting.valueFormat"></gljy-date-picker>
      <gljy-checkbox
        v-else-if="item.type == 'checkbox'"
        :class="itemClassName"
        :style="item.style ? item.style : { marginLeft: '0px', marginRight: '10px' }"
        @change="onFieldChange(item)"
        v-model="queryWhere[item.fieldName]"
        :true-value="1"
        :false-value="0">
        <div style="width: max-content;">{{ item.placeholder }}</div>
      </gljy-checkbox>
      <slot :name="slotAfterName" :field="item" :index="index"></slot>
    </template>
  </template>
</template>

<script>
export default {
  name: "gljy-query-where",
  emits: ["field-change"],
  props: {
    // 完整的查询条件字段配置数组(由父组件传入,内部根据 index 自动判断渲染范围)
    fields: {
      type: Array,
      default: () => {
        return [];
      },
    },
    // 整个查询条件对象(父组件传入的 reactive 对象,子组件直接修改属性即可双向同步)
    queryWhere: {
      type: Object,
      default: () => {
        return {};
      },
    },
    // 顶部展示的字段数量:index < 该值的字段由顶部渲染,其余由底部渲染
    topShowWhereFieldNumber: {
      type: Number,
      default: 4,
    },
    // 是否是底部展示:true 使用 searchItemBefore/After 插槽和 search-bottom-item 样式;
    // false(默认)使用 topSearchItemBefore/After 插槽,不添加额外样式类
    isBottom: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // 顶部/底部对应的前置插槽名
    slotBeforeName() {
      return this.isBottom ? "searchItemBefore" : "topSearchItemBefore";
    },
    // 顶部/底部对应的后置插槽名
    slotAfterName() {
      return this.isBottom ? "searchItemAfter" : "topSearchItemAfter";
    },
    // 顶部不添加额外类名,底部加 search-bottom-item 用于底部样式
    itemClassName() {
      return this.isBottom ? "search-bottom-item" : "";
    },
  },
  methods: {
    // 字段是否在当前展示位置可见:顶部 index < N 且未隐藏,底部 index >= N 且未隐藏
    isFieldVisible(item, index) {
      const notHidden = item.isHidden == null || item.isHidden == false || item.isHidden == "false";
      if (!notHidden) return false;
      return this.isBottom ? index >= this.topShowWhereFieldNumber : index < this.topShowWhereFieldNumber;
    },
    // 字段值变化事件,向上抛给父组件统一处理(是否自动查询等逻辑仍由父组件决定)
    onFieldChange(item) {
      this.$emit("field-change", item);
    },
  },
};
</script>